import { withRouter } from 'next/router';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SearchField from '@fuww/library/src/SearchField';
import Articles from '@fuww/library/src/Layouts/ArticleListPage';
import RadioButtons
from '@fuww/library/src/RadioButtons/SearchRadioButtons';
import { useIntl } from 'react-intl';
import { pick, without } from 'lodash';

import ArticleList from '../components/News/Article/List';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import withQuery from '../lib/withQuery';
import { useSiteContext } from '../components/SiteContext';
import Routes, { Router } from '../routes.mjs';
import getArticlesField from '../lib/queries/getConnectionField';
import articlesConnectionQuery
from '../lib/queries/news/articlesConnectionQuery.graphql';
import getNewsboardFieldVariables
from '../lib/queries/getNewsboardFieldVariables';
import { newsFeedImageConfigurations } from '../lib/imageConfigurations';
import Head from '../components/Head';
import messages from '../lib/messages.mjs';
import { getMessages } from '../lib/i18n.mjs';
import debouncedReplaceRoute from '../lib/debouncedReplaceRoute';
import withApollo from '../lib/apollo';
import replaceRoute from '../lib/replaceRoute';
import { EmbeddingsSearchModel } from '../lib/graphql/api/graphql';

const ArticleListWithQuery = withQuery(ArticleList);

const errorMessage = 'Error loading news articles...';

const ArticleSearchPage = ({
  router: { query },
}) => {
  const intl = useIntl();
  const { prefix, hasLocalNews, newsboard } = useSiteContext();

  const updateQueryParameters = (
    parameters,
    parameter,
    value,
    unsetValue,
  ) => ({
    searchPath: intl.formatMessage(messages.searchPath),
    ...pick(parameters, without([
      'keywords',
      'sort',
      'type',
      'model',
    ], parameter)),
    ...value && value !== unsetValue ? { [parameter]: value } : {},
  });

  const articlesField = getArticlesField(prefix, hasLocalNews);
  const {
    keywords: keywordsParameter, sort, type, model,
  } = query;
  const searchVariables = {
    ...getNewsboardFieldVariables(prefix, hasLocalNews),
    pageviews: false,
    newsboards: [newsboard],
    [type === 'AI' ? 'embeddingsSearch' : 'search']: keywordsParameter,
    embeddingsSearchModel: model ?? EmbeddingsSearchModel.E5,
    sort: sort || 'RELEVANCE_AND_DATE',
    first: 10,
    imageConfigurations: newsFeedImageConfigurations,
  };

  const [keywords, setKeywords] = useState(keywordsParameter);

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="search"
      passHref
    >
      {intl.formatMessage(messages.searchTitle)}
    </LinkAndAnchor>,
  ];

  const searchFields = (
    <>
      <SearchField
        id="search"
        name="search"
        label={intl.formatMessage(messages.searchTitle)}
        value={keywords}
        margin="normal"
        fullWidth
        onChange={({ target: { value } }) => {
          setKeywords(value);

          debouncedReplaceRoute(
            'search',
            updateQueryParameters(query, 'keywords', value, ''),
          );
        }}
        onClear={() => {
          setKeywords('');
          replaceRoute(
            'search',
            updateQueryParameters(query, 'keywords'),
          );
        }}
      />
      <RadioButtons
        name="sort"
        value={sort || 'RELEVANCE_AND_DATE'}
        onChange={({ target: { value } }) => replaceRoute('search',
          updateQueryParameters(query, 'sort', value, 'RELEVANCE_AND_DATE'),
        )}
        values={[
          {
            value: 'RELEVANCE_AND_DATE',
            label: intl.formatMessage(
              messages['search.sort.relevanceAndDate'],
            ),
          },
          {
            value: 'RELEVANCE',
            label: intl.formatMessage(
              messages['search.sort.relevance'],
            ),
          },
          {
            value: 'CREATED_AT',
            label: intl.formatMessage(
              messages['search.sort.createdAt'],
            ),
          },
          {
            value: 'CREATED_AT_ASCENDING',
            label: intl.formatMessage(
              messages['search.sort.createdAtAscending'],
            ),
          },
        ]}
      />
      <RadioButtons
        name="type"
        value={type === 'AI' ? 'AI' : 'TEXT'}
        onChange={({ target: { value } }) => replaceRoute(
          'search', updateQueryParameters(query, 'type', value, 'TEXT'),
        )}
        values={[
          {
            value: 'TEXT',
            label: intl.formatMessage(messages['search.type.text']),
          },
          {
            value: 'AI',
            label: intl.formatMessage(messages['search.type.ai']),
          },
        ]}
      />
    </>
  );

  return (
    <Articles
      title={intl.formatMessage(messages.searchTitle)}
      breadcrumbs={breadcrumbs}
      searchFields={searchFields}
    >
      <Head
        title={intl.formatMessage(messages.searchTitle)}
        description={intl.formatMessage(messages.indexDescription)}
        payload={query}
      />
      {keywordsParameter && (
      <ArticleListWithQuery
        query={articlesConnectionQuery}
        variables={searchVariables}
        errorMessage={errorMessage}
        field={articlesField}
      />
      )}
    </Articles>
  );
};

const route = 'search';

ArticleSearchPage.getInitialProps = async ({
  res, req, query: { searchPath, prefix },
}) => {
  const { searchPath: actualSearchPath } = getMessages(
    req
      ? req.locale
      // eslint-disable-next-line no-underscore-dangle
      : window.__NEXT_DATA__.props.locale,
  );
  const query = prefix
    ? { searchPath: actualSearchPath, prefix }
    : { searchPath: actualSearchPath };

  if (searchPath !== actualSearchPath) {
    if (res) {
      res.writeHead(301, {
        Location: Routes.findAndGetUrls(
          route,
          query,
        ).urls.as,
      });
      res.end();
    } else {
      Router.pushRoute(route, query);
    }
  }

  return {};
};

ArticleSearchPage.propTypes = {
  router: PropTypes.shape({
    query: PropTypes.shape({
      searchPath: PropTypes.string,
      prefix: PropTypes.string,
      keywords: PropTypes.string,
      sort: PropTypes.string,
      type: PropTypes.string,
      model: PropTypes.string,
    }),
  }),
};

ArticleSearchPage.defaultProps = {
  router: {},
};

export const Search = withRouter(ArticleSearchPage);

export default withApollo(Search);
